// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import { format, formatDistanceToNow } from 'date-fns';

interface ActivityItem {
  id: string;
  type: 'user_registered' | 'topic_created' | 'subscription_added' | 'notification_sent';
  description: string;
  timestamp: string;
  user?: string;
}

export const AdminActivityFeed: React.FC = () => {
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRecentActivities();

    // Set up real-time subscription for activities
    const channel = supabase
      .channel('admin-activity-feed')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'profiles',
        },
        (payload) => {
          setActivities((prev) => [
            {
              id: payload.new.id,
              type: 'user_registered',
              description: 'New user registered',
              timestamp: payload.new.created_at || new Date().toISOString(),
            },
            ...prev.slice(0, 9),
          ]);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'law_topics',
        },
        (payload) => {
          setActivities((prev) => [
            {
              id: payload.new.id,
              type: 'topic_created',
              description: `New topic created: ${payload.new.name}`,
              timestamp: payload.new.created_at || new Date().toISOString(),
            },
            ...prev.slice(0, 9),
          ]);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'user_topic_subscriptions',
        },
        () => {
          setActivities((prev) => [
            {
              id: Date.now().toString(),
              type: 'subscription_added',
              description: 'New subscription added',
              timestamp: new Date().toISOString(),
            },
            ...prev.slice(0, 9),
          ]);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
        },
        () => {
          setActivities((prev) => [
            {
              id: Date.now().toString(),
              type: 'notification_sent',
              description: 'Notification sent to user',
              timestamp: new Date().toISOString(),
            },
            ...prev.slice(0, 9),
          ]);
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, []);

  const loadRecentActivities = async () => {
    setLoading(true);
    try {
      // Get recent user registrations
      const { data: recentUsers } = await supabase
        .from('profiles')
        .select('id, created_at')
        .order('created_at', { ascending: false })
        .limit(5);

      // Get recent topics
      const { data: recentTopics } = await supabase
        .from('law_topics')
        .select('id, name, created_at')
        .order('created_at', { ascending: false })
        .limit(3);

      // Get recent subscriptions
      const { data: recentSubscriptions } = await supabase
        .from('user_topic_subscriptions')
        .select('id, created_at')
        .order('created_at', { ascending: false })
        .limit(3);

      const activitiesList: ActivityItem[] = [
        ...(recentUsers?.map((user) => ({
          id: user.id,
          type: 'user_registered' as const,
          description: 'New user registered',
          timestamp: user.created_at,
        })) || []),
        ...(recentTopics?.map((topic) => ({
          id: topic.id,
          type: 'topic_created' as const,
          description: `New topic created: ${topic.name}`,
          timestamp: topic.created_at,
        })) || []),
        ...(recentSubscriptions?.map((sub) => ({
          id: sub.id,
          type: 'subscription_added' as const,
          description: 'New subscription added',
          timestamp: sub.created_at,
        })) || []),
      ]
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
        .slice(0, 10);

      setActivities(activitiesList);
    } catch (err) {
      console.error('Error loading activities:', err);
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (type: ActivityItem['type']) => {
    switch (type) {
      case 'user_registered':
        return 'person_add';
      case 'topic_created':
        return 'topic';
      case 'subscription_added':
        return 'subscriptions';
      case 'notification_sent':
        return 'notifications';
      default:
        return 'circle';
    }
  };

  const getActivityColor = (type: ActivityItem['type']) => {
    switch (type) {
      case 'user_registered':
        return 'text-blue-500';
      case 'topic_created':
        return 'text-green-500';
      case 'subscription_added':
        return 'text-purple-500';
      case 'notification_sent':
        return 'text-orange-500';
      default:
        return 'text-gray-500';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-luna-secondary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-2 sm:space-y-3">
      <h3 className="text-base sm:text-lg font-bold text-luna-text-light dark:text-luna-text-dark mb-3 sm:mb-4">
        Recent Activity
      </h3>
      {activities.length === 0 ? (
        <p className="text-xs sm:text-sm text-luna-text-light/50 dark:text-luna-text-dark/50 text-center py-4">
          No recent activity
        </p>
      ) : (
        <div className="max-h-[400px] sm:max-h-[500px] overflow-y-auto overscroll-y-contain">
          {activities.map((activity) => (
            <div
              key={activity.id}
              className="flex items-start gap-2 sm:gap-3 p-2.5 sm:p-3 rounded-lg hover:bg-luna-secondary/5 active:bg-luna-secondary/10 transition-colors touch-manipulation"
            >
              <span
                className={`material-symbols-outlined text-lg sm:text-xl flex-shrink-0 ${getActivityColor(activity.type)}`}
              >
                {getActivityIcon(activity.type)}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-xs sm:text-sm text-luna-text-light dark:text-luna-text-dark leading-relaxed">
                  {activity.description}
                </p>
                <p className="text-xs text-luna-text-light/50 dark:text-luna-text-dark/50 mt-1">
                  {formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

